/**
 * Re-export of shared prompt-type labels and helpers. Kept as a barrel so
 * existing admin imports (`../../types/promptTypeLabels`) keep working;
 * the source of truth is `Backend/shared/src/promptTypes.ts`.
 *
 * `PROMPT_TYPE_LABELED_OPTIONS` is a small admin-only convenience derived
 * from the shared registries.
 */

import {
    PROMPT_TYPES,
    promptTypeLabel,
    PROMPT_TYPE_SORT_INDEX,
    OPERATION_TYPE_KEYS,
    OPERATION_STEP_TYPES,
    operationLabel,
    operationsForStepType,
} from '@padcanvas/shared';
import type { PromptType } from '@padcanvas/shared';

export { promptTypeLabel, PROMPT_TYPE_SORT_INDEX };

/** Alias kept for callers that want a stable "options for the Type
 *  filter / Create dropdown" list. Same as `PROMPT_TYPES` from shared. */
export const PROMPT_TYPE_OPTIONS: PromptType[] = [...PROMPT_TYPES];

/** `{ value, label }` pairs — convenience for `<select>` / dropdowns
 *  that want both pieces in one shot. */
export const PROMPT_TYPE_LABELED_OPTIONS: { value: PromptType; label: string }[] =
    PROMPT_TYPES.map(t => ({ value: t, label: promptTypeLabel(t) }));

/**
 * The same list, nested one level under the operation that runs each prompt —
 * but only where nesting earns its keep.
 *
 * Thirty-two prompt types in one flat dropdown is a list nobody can scan; what
 * a reader actually holds in their head is the operation ("what does hinting
 * run?"). The grouping is derived from `OPERATION_STEP_TYPES`, so it cannot
 * drift from the pipeline config the way a hand-kept menu does.
 *
 * AN OPERATION THAT RUNS ONE PROMPT IS NOT A GROUP. A heading above a single
 * child adds a line to scan and says nothing the child did not already say —
 * and most operations here run exactly one prompt, so honouring the hierarchy
 * everywhere would double the list's height to express nothing. Those types are
 * offered flat and alphabetically, after the handful of operations that really
 * do run several. Alphabetical because a flat run has no pipeline order left to
 * preserve: the operation sequence was the only thing that ordered them, and
 * once the heading is gone that order is invisible and reads as random.
 *
 * Two consequences of the real model, deliberately visible here rather than
 * smoothed over:
 *   - a prompt used by two operations is LISTED under both — `extract_steps`
 *     and `extract_visuals` are the shared pre-step of analysis and hint, and
 *     `validate_extraction` judges either extraction flavour. Both entries
 *     filter identically.
 *   - a prompt no operation runs joins the flat tail. Today that is
 *     `detect_page_orientation`, which supplies its own operation config inline
 *     from `shared/imageOrientation.ts`.
 */

/** The prompt-running steps of an operation. Compute steps (`score_*`) sit in
 *  the same registry but run no prompt, so they are not offered as a type. */
function promptStepsOf(op: (typeof OPERATION_TYPE_KEYS)[number]): PromptType[] {
    return OPERATION_STEP_TYPES[op].filter(
        (st): st is PromptType => (PROMPT_TYPES as readonly string[]).includes(st),
    );
}

export const PROMPT_TYPE_GROUPED_OPTIONS: { value: PromptType; label: string; group?: string }[] = [
    ...OPERATION_TYPE_KEYS
        .filter(op => promptStepsOf(op).length > 1)
        .flatMap(op =>
            promptStepsOf(op).map(st => ({
                value: st,
                label: promptTypeLabel(st),
                group: operationLabel(op),
            })),
        ),
    ...[
        ...OPERATION_TYPE_KEYS.filter(op => promptStepsOf(op).length === 1).flatMap(promptStepsOf),
        ...PROMPT_TYPES.filter(t => operationsForStepType(t).length === 0),
    ]
        .filter((t, i, all) => all.indexOf(t) === i)
        .map(t => ({ value: t, label: promptTypeLabel(t) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
];

/**
 * The nested list for pickers that CREATE or RETARGET a prompt rather than
 * filter one — each type appears once, under the first operation that runs it.
 *
 * Filtering by a prompt used in two operations is the same filter either way,
 * so `PROMPT_TYPE_GROUPED_OPTIONS` lists it twice on purpose. Creating one is
 * a single act: offering "Extract Steps" under both Solution Analysis and Hint
 * would be two buttons that do the same thing, and picking the second would
 * read as creating a hint-specific prompt, which is not a thing that exists.
 */
export const PROMPT_TYPE_GROUPED_UNIQUE: { value: PromptType; label: string; group?: string }[] =
    PROMPT_TYPE_GROUPED_OPTIONS.filter(
        (o, i, all) => all.findIndex(x => x.value === o.value) === i,
    );

/**
 * The same deduped list collected into the runs the markup renders (a popover,
 * an `<optgroup>`).
 *
 * The single-prompt rule is applied AFTER deduplication, not before: dedup can
 * leave an operation with one entry that had two — Question Extraction (Word)
 * runs `validate_extraction`, but analysis-side already listed it — and a
 * heading over one child is as empty here as anywhere. Whatever is left alone
 * joins the same flat alphabetical tail.
 */
export const PROMPT_TYPE_GROUPS: { group?: string; options: typeof PROMPT_TYPE_GROUPED_UNIQUE }[] =
    (() => {
        const runs = PROMPT_TYPE_GROUPED_UNIQUE.reduce((acc, o) => {
            const last = acc[acc.length - 1];
            if (last && last.group === o.group) last.options.push(o);
            else acc.push({ group: o.group, options: [o] });
            return acc;
        }, [] as { group?: string; options: typeof PROMPT_TYPE_GROUPED_UNIQUE }[]);

        const grouped = runs.filter(r => r.group && r.options.length > 1);
        const flat = runs
            .filter(r => !(r.group && r.options.length > 1))
            .flatMap(r => r.options)
            .map(({ group: _g, ...o }) => o)
            .sort((a, b) => a.label.localeCompare(b.label));

        return flat.length > 0 ? [...grouped, { options: flat }] : grouped;
    })();
